import React from 'react';

/**
 * Convert URLs in text to clickable links
 * @param {string} text - The text containing URLs
 * @param {string} className - Optional class for the link
 * @returns {React.ReactNode[]} Array of text and link elements
 */
export const linkifyText = (text, linkClassName = "text-blue-400 underline hover:text-blue-300 break-all") => {
    if (!text) return null;
    
    // URL regex pattern - matches http, https, and www URLs
    const urlPattern = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
    
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    
    // Create a copy of the regex for iteration
    const regex = new RegExp(urlPattern);
    
    while ((match = regex.exec(text)) !== null) {
        // Add text before the URL
        if (match.index > lastIndex) {
            parts.push(
                <span key={`text-${key++}`}>
                    {text.slice(lastIndex, match.index)}
                </span>
            );
        }
        
        // Add the URL as a clickable link
        let url = match[0];
        let href = url;
        
        // Add protocol if missing (for www. links)
        if (url.startsWith('www.')) {
            href = 'https://' + url;
        }
        
        parts.push(
            <a
                key={`link-${key++}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClassName}
                onClick={(e) => e.stopPropagation()}
            >
                {url}
            </a>
        );
        
        lastIndex = regex.lastIndex;
    }
    
    // Add remaining text after the last URL
    if (lastIndex < text.length) {
        parts.push(
            <span key={`text-${key++}`}>
                {text.slice(lastIndex)}
            </span>
        );
    }
    
    // If no URLs found, return the original text
    if (parts.length === 0) {
        return text;
    }
    
    return parts;
};

/**
 * Extract domain name from URL for display
 * @param {string} url - The URL
 * @returns {string} The domain name
 */
export const extractDomain = (url) => {
    try {
        const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
        return urlObj.hostname.replace('www.', '');
    } catch {
        return url;
    }
};

/**
 * Check if a string contains a URL
 * @param {string} text - The text to check
 * @returns {boolean} True if contains URL
 */
export const containsUrl = (text) => {
    if (!text) return false;
    const urlPattern = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
    return urlPattern.test(text);
};

/**
 * Extract first URL from text
 * @param {string} text - The text containing URLs
 * @returns {string|null} The first URL found or null
 */
export const extractFirstUrl = (text) => {
    if (!text) return null;
    const urlPattern = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
    const match = text.match(urlPattern);
    if (match && match[0]) {
        let url = match[0];
        if (url.startsWith('www.')) {
            url = 'https://' + url;
        }
        return url;
    }
    return null;
};

export default { linkifyText, extractDomain, containsUrl, extractFirstUrl };
